// src/components/RelatedArticles.jsx

import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { useArticlesContext } from "../context/ArticlesContext";

/**
 * A component that displays 2 articles most "related" to a reference article,
 * based on the number of shared labels.
 *
 * @param {Object} props
 * @param {Object} props.referenceArticle - The article for which we want to find related articles.
 */
const RelatedArticles = ({ referenceArticle }) => {
  const { articles } = useArticlesContext();

  /**
   * Computes and returns the top 2 articles with the largest label overlap
   * relative to the referenceArticle.
   */
  const related = useMemo(() => {
    if (!referenceArticle || !referenceArticle.labels) return [];

    // 1) Extract labels from the reference article.
    const refLabels = referenceArticle.labels; // array of label IDs

    // 2) Filter out the reference article so it doesn't match itself.
    const otherArticles = articles.filter(
      (art) => art.slug !== referenceArticle.slug
    );

    // 3) Calculate the overlap in labels for each candidate article.
    const scored = otherArticles.map((art) => {
      const overlapCount = art.labels
        ? art.labels.filter((labelId) => refLabels.includes(labelId)).length
        : 0;
      return { ...art, overlapCount };
    });

    // 4) Sort by overlapCount descending; then take top 2.
    scored.sort((a, b) => b.overlapCount - a.overlapCount);
    return scored.slice(0, 2);
  }, [articles, referenceArticle]);

  // Before rendering, transform each related article to have a .imageOriginal 
  // plus a -low.jpg version, similar to ArticlesList approach.
  const processedRelated = useMemo(() => {
    return related.map((art) => {
      const copy = { ...art };
      copy.imageOriginal = copy.image;
      if (copy.image && copy.image.endsWith(".jpg")) {
        copy.image = copy.image.replace(".jpg", "-low.jpg");
      }
      return copy;
    });
  }, [related]);

  // If no related articles are found, return nothing
  if (processedRelated.length === 0) {
    return null;
  }

  return (
    <section className="related-articles-module">
      <h2 className="related-articles-title">Related Articles</h2>
      <div className="related-articles-grid">
        {processedRelated.map((art) => (
          <article key={art.slug} className="related-article-item">
            <Link to={`/articles/${art.slug}`} className="related-article-image-link">
              <img
                src={art.image}
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = art.imageOriginal;
                }}
                alt={art.title}
                className="related-article-image"
              />
              <div className="related-article-overlay">
                <span className="related-article-overlay-text">Read More</span>
              </div>
            </Link>
            <div className="related-article-info">
              <h3 className="related-article-heading">
                <Link to={`/articles/${art.slug}`}>{art.title}</Link>
              </h3>
              <p className="related-article-excerpt">{art.excerpt}</p>
              <div className="related-article-footer">
                <Link to={`/articles/${art.slug}`} className="related-article-readmore">
                  Read Full Article
                </Link>
              </div>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
};

export default RelatedArticles;